// @ts-nocheck
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Calendar as CalendarIcon, 
  AlertTriangle, 
  Clock, 
  Users, 
  Building2,
  Scale,
  FileText,
  DollarSign,
  MapPin
} from 'lucide-react';
import { SimpleFeeConfig } from '@/components/financial/SimpleFeeConfig';
import { SmartContractConfig } from '@/components/financial/SmartContractConfig';
import type { ContractConfiguration } from '@/types/ContractConfiguration';
import { formatBRLFromDigits, parseBRLStringToNumber } from '@/utils/brlFormat';

interface CaseFormData {
  // Identificação do Processo
  case_number: string;
  case_number_cnj: string; // Numeração CNJ
  internal_number: string; // Numeração interna do escritório
  title: string;
  description: string;
  
  // Classificação
  case_type: string;
  area_of_law: string;
  subject_matter: string;
  case_value: string;
  contract_value: number; // Valor do contrato para base de cálculo
  risk_classification: 'baixo' | 'medio' | 'alto' | 'critico';
  
  // Tribunal e Localização
  court: string;
  court_level: '1_instancia' | '2_instancia' | 'superior' | 'supremo';
  jurisdiction: string;
  vara: string;
  forum: string;
  
  // Datas Importantes
  filing_date?: Date;
  deadline?: Date;
  next_hearing?: Date;
  statute_limitations?: Date;
  
  // Status e Prioridade
  status: string;
  phase: string;
  priority: string;
  confidentiality_level: 'normal' | 'restrito' | 'secreto';
  
  // Responsáveis
  lawyer_responsible: string;
  client_id: string;
  opposing_party: string;
  opposing_counsel: string;
  
  // Litisconsórcio
  is_litisconsorcio: boolean;
  selected_clients: Array<{ id: string; name: string; email: string; is_primary: boolean }>;
  
  // Valores e Custos (Campos Financeiros Básicos)
  client_fee: string;
  court_costs: string;
  estimated_duration: string;
  success_fee?: string;
  
  // Configuração de Contrato (NOVO SISTEMA)
  contractConfiguration?: ContractConfiguration;
  
  // Configuração Financeira Avançada (LEGADO - será migrado)
  feeConfiguration: {
    // Honorários Iniciais
    hasInitialFee: boolean;
    initialFeeAmount: number;
    initialFeeType: 'percentage' | 'fixed';
    initialFeeBaseValue: number;
    initialFeePaymentStatus: 'pending' | 'paid' | 'partial';

    // Honorários de Êxito
    hasSuccessFee: boolean;
    successFeeType: 'percentage_global' | 'percentage_installment' | 'fixed_global' | 'fixed_monthly';
    successFeeValue: number;
    successFeeTrigger: 'sentenca' | 'acordo' | 'liminar' | 'recebimento' | 'decisao' | 'outro';
    successFeeCalculationBase: 'total' | 'per_installment';

    // Benefício
    benefitValue: number;
    benefitValueType: 'estimated' | 'confirmed' | 'undefined';
    benefitPaymentType: 'lump_sum' | 'installments' | 'undefined';
    benefitInstallmentCount: number;
    benefitInstallmentValue: number;
    benefitFirstPaymentDate?: Date;
    benefitFirstPaymentDatePending: boolean;

    // Benefício Indefinido
    isBenefitUndefined: boolean;
    undefinedBenefitFirstInstallmentsCount: number;
    undefinedBenefitFirstInstallmentValue: number;

    // Pagamento Retroativo
    hasRetroactivePayment: boolean;
    retroactivePaymentValue: number;
    retroactivePaymentType: 'percentage' | 'fixed';
    retroactivePaymentDate?: Date;
    retroactivePaymentDatePending: boolean;
    chargeOnRetroactive: boolean;
    chargeOnInstallments: boolean;
    chargeOnBoth: boolean;

    // Pro Bono
    isProBono: boolean;
  };
  
  // Observações
  strategy_notes: string;
  internal_notes: string;
  client_objectives: string;
  
  // Controle de Prazos
  monitoring_deadlines: boolean;
  critical_deadlines: string[];
  alert_days_before: number;
  
  // Arquivamento e Tags
  tags: string[];
  archived: boolean;
  archive_reason?: string;
}

interface ProcessFormProps {
  formData: CaseFormData;
  setFormData: React.Dispatch<React.SetStateAction<CaseFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isEditing: boolean;
  clients: Array<{ id: string; name: string; email: string }>;
}

const caseTypes = [
  'Trabalhista', 'Civil', 'Criminal', 'Tributário', 'Empresarial', 
  'Família e Sucessões', 'Previdenciário', 'Consumidor', 'Ambiental',
  'Administrativo', 'Constitucional', 'Internacional'
];

// Área do Direito correspondente a cada tipo (um único campo no formulário preenche os dois)
const areaOfLawByType: Record<string, string> = {
  'Trabalhista': 'Direito Trabalhista',
  'Civil': 'Direito Civil',
  'Criminal': 'Direito Penal',
  'Tributário': 'Direito Tributário',
  'Empresarial': 'Direito Empresarial',
  'Família e Sucessões': 'Direito de Família',
  'Previdenciário': 'Direito Previdenciário',
  'Consumidor': 'Direito do Consumidor',
  'Ambiental': 'Direito Ambiental',
  'Administrativo': 'Direito Administrativo',
  'Constitucional': 'Direito Constitucional',
  'Internacional': 'Direito Internacional',
};

const statusOptions = [
  'Distribuído', 'Em andamento', 'Aguardando documentos', 'Aguardando manifestação',
  'Aguardando audiência', 'Aguardando sentença', 'Aguardando recurso',
  'Em recurso', 'Transitado em julgado', 'Arquivado', 'Suspenso'
];

const phaseOptions = [
  'Petição inicial', 'Citação', 'Contestação', 'Tréplica', 'Saneador',
  'Instrução', 'Audiência', 'Sentença', 'Recurso', 'Execução', 'Cumprimento'
];

const courtLevels = [
  { value: '1_instancia', label: '1ª Instância' },
  { value: '2_instancia', label: '2ª Instância' },
  { value: 'superior', label: 'Tribunais Superiores' },
  { value: 'supremo', label: 'Supremo Tribunal Federal' }
];

const riskLevels = [
  { value: 'baixo', label: 'Baixo', color: 'bg-green-100 text-green-800' },
  { value: 'medio', label: 'Médio', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'alto', label: 'Alto', color: 'bg-orange-100 text-orange-800' },
  { value: 'critico', label: 'Crítico', color: 'bg-red-100 text-red-800' }
];

const priorityOptions = ['Baixa', 'Média', 'Alta', 'Urgente'];

export const EnhancedCaseForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isEditing,
  clients 
}: ProcessFormProps) => {

  // Função para formatar valores em Real brasileiro
  const formatCurrency = (value: string | number) => {
    // Se já é um número, formata diretamente
    if (typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    
    // Se é string, verifica se já está formatada (contém R$ ou vírgula)
    if (typeof value === 'string' && (value.includes('R$') || value.includes(','))) {
      // Já está formatada, retorna como está
      return value;
    }
    
    // Remove tudo que não é número
    const numericValue = value.replace(/\D/g, '');
    
    if (numericValue === '') return '';
    
    // Converte para número - NÃO divide por 100 se for valor direto
    const number = parseFloat(numericValue);
    
    // Formata como Real brasileiro
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  // Função para limpar formatação e retornar apenas números
  const unformatCurrency = (value: string) => {
    return value.replace(/\D/g, '');
  };

  // Função para formatar percentual
  const formatPercentage = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    if (numericValue === '') return '';
    return `${numericValue}%`;
  };

  // Função para limpar formatação de percentual
  const unformatPercentage = (value: string) => {
    return value.replace(/\D/g, '');
  };
  const [activeTab, setActiveTab] = useState('identification');

  const formatCaseNumberCNJ = (value: string) => {
    // Formato CNJ: NNNNNNN-DD.AAAA.J.TR.OOOO
    const numbers = value.replace(/\D/g, '');
    if (numbers.length >= 20) {
      return numbers.replace(/(\d{7})(\d{2})(\d{4})(\d{1})(\d{2})(\d{4})/, '$1-$2.$3.$4.$5.$6');
    }
    return value;
  };

  const handleTagToggle = (tag: string) => {
    const currentTags = formData.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    
    setFormData({ ...formData, tags: newTags });
  };

  const calculateDaysUntilDeadline = () => {
    if (!formData.deadline) return null;
    const today = new Date();
    const deadline = new Date(formData.deadline);
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const commonTags = [
    'Urgente', 'Estratégico', 'Alto valor', 'Mídia', 'Precedente',
    'Complexo', 'Recorrente', 'VIP', 'Pro bono', 'Consultivo'
  ];

  return (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="mb-6">
          <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-6">
        <TabsTrigger 
          value="identification" 
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Identificação
        </TabsTrigger>
        <TabsTrigger 
          value="classification"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Classificação
        </TabsTrigger>
        <TabsTrigger 
          value="court"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Tribunal
        </TabsTrigger>
        <TabsTrigger 
          value="parties"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Partes
        </TabsTrigger>
        <TabsTrigger 
          value="deadlines"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Prazos
        </TabsTrigger>
        <TabsTrigger 
          value="financial"
          className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm"
        >
          Financeiro
                  </TabsTrigger>
        </TabsList>
        </div>

      <form onSubmit={onSubmit} className="space-y-6">
        <TabsContent value="identification" className="space-y-4">
          <Card className="bg-white shadow-lg border border-[#c4ab83]/30 hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-[#c4ab83]/20 to-[#c4ab83]/10 border-b border-[#c4ab83]/50 backdrop-blur-md bg-white/30 shadow-lg">
              <CardTitle className="flex items-center gap-3 text-xl font-bold text-black drop-shadow-sm">
                <Scale className="w-6 h-6" />
                Identificação do Processo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="case_number_cnj" className="text-gray-800 font-semibold text-sm">Número CNJ *</Label>
                  <Input
                    id="case_number_cnj"
                    value={formData.case_number_cnj}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      case_number_cnj: formatCaseNumberCNJ(e.target.value) 
                    })}
                    placeholder="0000000-00.0000.0.00.0000"
                    required
                    className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                  />
                  <p className="text-xs text-blue-600/80 mt-1 font-medium">
                    Formato: NNNNNNN-DD.AAAA.J.TR.OOOO
                  </p>
                </div>
                <div>
                  <Label htmlFor="internal_number" className="text-gray-800 font-semibold text-sm">Número Interno</Label>
                  <Input
                    id="internal_number"
                    value={formData.internal_number}
                    onChange={(e) => setFormData({ ...formData, internal_number: e.target.value })}
                    placeholder="Numeração interna do escritório"
                    className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="title" className="text-gray-800 font-semibold text-sm">Título do Processo *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Ação de Cobrança - Empresa XYZ"
                  required
                  className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-gray-800 font-semibold text-sm">Descrição Detalhada</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  placeholder="Descreva o objeto da ação, pedidos principais, contexto..."
                  className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-800 font-semibold text-sm">Data de Distribuição</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-white border-gray-300 text-gray-900 hover:bg-blue-50 hover:border-blue-500",
                          !formData.filing_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.filing_date ? format(formData.filing_date, "PPP", { locale: ptBR }) : "Selecione a data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.filing_date}
                        onSelect={(date) => setFormData({ ...formData, filing_date: date })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="case_value" className="text-gray-800 font-semibold text-sm">Valor da Causa</Label>
                  <Input
                    id="case_value"
                    inputMode="decimal"
                    value={formData.case_value}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        case_value: formatBRLFromDigits(e.target.value),
                      });
                    }}
                    placeholder="0,00"
                    className="bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="classification" className="space-y-4">
          <Card className="bg-white border border-[#c4ab83]/30">
            <CardHeader>
              <CardTitle>Classificação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="md:w-1/2">
                <Label htmlFor="case_type">Área do Direito *</Label>
                <Select
                  value={formData.case_type || ''}
                  onValueChange={(value) =>
                    setFormData({ ...formData, case_type: value, area_of_law: areaOfLawByType[value] || value })
                  }
                >
                  <SelectTrigger id="case_type">
                    <SelectValue placeholder="Selecione a área" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Processos importados podem ter um tipo fora da lista; mantém o valor atual visível */}
                    {formData.case_type && !caseTypes.includes(formData.case_type) && (
                      <SelectItem value={formData.case_type}>{formData.case_type}</SelectItem>
                    )}
                    {caseTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="risk_classification">Classificação de Risco</Label>
                  <Select value={formData.risk_classification || ''} onValueChange={(value) => setFormData({ ...formData, risk_classification: value as any })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {riskLevels.map((risk) => (
                        <SelectItem key={risk.value} value={risk.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${risk.color.split(' ')[0]}`} />
                            {risk.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="priority">Prioridade</Label>
                  <Select value={formData.priority || ''} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map((priority) => (
                        <SelectItem key={priority} value={priority}>
                          {priority}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="confidentiality_level">Nível de Sigilo</Label>
                  <Select value={formData.confidentiality_level || ''} onValueChange={(value) => setFormData({ ...formData, confidentiality_level: value as any })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="restrito">Restrito</SelectItem>
                      <SelectItem value="secreto">Secreto de Justiça</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Tags do Processo</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                  {commonTags.map((tag) => (
                    <div key={tag} className="flex items-center space-x-2">
                      <Checkbox
                        id={tag}
                        checked={formData.tags?.includes(tag) || false}
                        onCheckedChange={() => handleTagToggle(tag)}
                      />
                      <Label htmlFor={tag} className="text-sm">
                        {tag}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="court" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Tribunal e Jurisdição
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="court">Tribunal/Comarca</Label>
                  <Input
                    id="court"
                    value={formData.court}
                    onChange={(e) => setFormData({ ...formData, court: e.target.value })}
                    placeholder="Ex: TJSP - São Paulo"
                  />
                </div>
                <div>
                  <Label htmlFor="court_level">Instância</Label>
                  <Select value={formData.court_level || ''} onValueChange={(value) => setFormData({ ...formData, court_level: value as any })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a instância" />
                    </SelectTrigger>
                    <SelectContent>
                      {courtLevels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vara">Vara/Câmara</Label>
                  <Input
                    id="vara"
                    value={formData.vara}
                    onChange={(e) => setFormData({ ...formData, vara: e.target.value })}
                    placeholder="Ex: 1ª Vara Cível"
                  />
                </div>
                <div>
                  <Label htmlFor="forum">Fórum</Label>
                  <Input
                    id="forum"
                    value={formData.forum}
                    onChange={(e) => setFormData({ ...formData, forum: e.target.value })}
                    placeholder="Ex: Fórum Central"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Status Processual</Label>
                  <Select value={formData.status || ''} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="phase">Fase Processual</Label>
                  <Select value={formData.phase || ''} onValueChange={(value) => setFormData({ ...formData, phase: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {phaseOptions.map((phase) => (
                        <SelectItem key={phase} value={phase}>
                          {phase}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="parties" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Partes e Responsáveis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Litisconsórcio */}
              <div className="flex items-center space-x-2 mb-4">
                <Checkbox
                  id="is_litisconsorcio"
                  checked={formData.is_litisconsorcio}
                  onCheckedChange={(checked) => {
                    const newValue = checked as boolean;
                    setFormData({ 
                      ...formData, 
                      is_litisconsorcio: newValue,
                      // Se desmarcar litisconsórcio, manter apenas o primeiro cliente
                      selected_clients: newValue ? formData.selected_clients : 
                        formData.selected_clients.slice(0, 1)
                    });
                  }}
                />
                <Label htmlFor="is_litisconsorcio" className="text-sm font-medium">
                  Este processo é um litisconsórcio (múltiplos clientes)
                </Label>
              </div>

              {/* Seleção de Clientes */}
              <div className="space-y-4">
                <div>
                  <Label>Clientes {formData.is_litisconsorcio ? '(Múltiplos)' : '(Principal)'} *</Label>
                  
                  {/* Cliente Principal (quando não é litisconsórcio) */}
                  {!formData.is_litisconsorcio && (
                    <div className="mt-2">
                      <Select 
                        value={formData.selected_clients[0]?.id || ''} 
                        onValueChange={(value) => {
                          const selectedClient = clients.find(c => c.id === value);
                          if (selectedClient) {
                            setFormData({
                              ...formData,
                              selected_clients: [{ ...selectedClient, is_primary: true }]
                            });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o cliente principal" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Múltiplos Clientes (quando é litisconsórcio) */}
                  {formData.is_litisconsorcio && (
                    <div className="space-y-3">
                      {/* Lista de clientes selecionados */}
                      {formData.selected_clients.map((client, index) => (
                        <div key={client.id} className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50">
                          <div className="flex-1">
                            <div className="font-medium">{client.name}</div>
                            <div className="text-sm text-gray-500">{client.email}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={client.is_primary}
                              onCheckedChange={(checked) => {
                                const updatedClients = formData.selected_clients.map((c, i) => ({
                                  ...c,
                                  is_primary: i === index ? checked as boolean : false
                                }));
                                setFormData({ ...formData, selected_clients: updatedClients });
                              }}
                            />
                            <Label className="text-xs">Principal</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const updatedClients = formData.selected_clients.filter((_, i) => i !== index);
                                setFormData({ ...formData, selected_clients: updatedClients });
                              }}
                            >
                              Remover
                            </Button>
                          </div>
                        </div>
                      ))}

                      {/* Adicionar novo cliente */}
                      <div className="flex items-center gap-2">
                        <Select
                          value=""
                          onValueChange={(value) => {
                            const selectedClient = clients.find(c => c.id === value);
                            if (selectedClient && !formData.selected_clients.find(c => c.id === value)) {
                              setFormData({
                                ...formData,
                                selected_clients: [
                                  ...formData.selected_clients,
                                  { ...selectedClient, is_primary: formData.selected_clients.length === 0 }
                                ]
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Selecione um cliente para adicionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {clients
                              .filter(client => !formData.selected_clients.find(c => c.id === client.id))
                              .map((client) => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="lawyer_responsible">Advogado Responsável</Label>
                  <Input
                    id="lawyer_responsible"
                    value={formData.lawyer_responsible}
                    onChange={(e) => setFormData({ ...formData, lawyer_responsible: e.target.value })}
                    placeholder="Nome do advogado principal"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="opposing_party">Parte Contrária</Label>
                  <Input
                    id="opposing_party"
                    value={formData.opposing_party}
                    onChange={(e) => setFormData({ ...formData, opposing_party: e.target.value })}
                    placeholder="Nome da parte adversa"
                  />
                </div>
                <div>
                  <Label htmlFor="opposing_counsel">Advogado da Parte Contrária</Label>
                  <Input
                    id="opposing_counsel"
                    value={formData.opposing_counsel}
                    onChange={(e) => setFormData({ ...formData, opposing_counsel: e.target.value })}
                    placeholder="Nome do escritório/advogado adverso"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deadlines" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Controle de Prazos
                {formData.deadline && (
                  <Badge variant={calculateDaysUntilDeadline()! < 7 ? "destructive" : "secondary"}>
                    {calculateDaysUntilDeadline()} dias
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Próximo Prazo</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.deadline && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.deadline ? format(formData.deadline, "PPP", { locale: ptBR }) : "Selecione a data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.deadline}
                        onSelect={(date) => setFormData({ ...formData, deadline: date })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Próxima Audiência</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.next_hearing && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.next_hearing ? format(formData.next_hearing, "PPP", { locale: ptBR }) : "Selecione a data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.next_hearing}
                        onSelect={(date) => setFormData({ ...formData, next_hearing: date })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Prazo Prescricional</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.statute_limitations && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.statute_limitations ? format(formData.statute_limitations, "PPP", { locale: ptBR }) : "Selecione a data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.statute_limitations}
                        onSelect={(date) => setFormData({ ...formData, statute_limitations: date })}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="alert_days_before">Alertar com Antecedência (dias)</Label>
                  <Input
                    id="alert_days_before"
                    type="number"
                    value={formData.alert_days_before}
                    onChange={(e) => setFormData({ ...formData, alert_days_before: parseInt(e.target.value) || 5 })}
                    placeholder="5"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="monitoring_deadlines"
                  checked={formData.monitoring_deadlines}
                  onCheckedChange={(checked) => setFormData({ ...formData, monitoring_deadlines: checked as boolean })}
                />
                <Label htmlFor="monitoring_deadlines">
                  Monitorar prazos automaticamente
                </Label>
              </div>

              <div>
                <Label htmlFor="estimated_duration">Duração Estimada</Label>
                <Input
                  id="estimated_duration"
                  value={formData.estimated_duration}
                  onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
                  placeholder="Ex: 6 meses, 1 ano"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financial" className="space-y-4 min-h-0">
          <SimpleFeeConfig
            caseValue={parseBRLStringToNumber(formData.case_value || '0')}
            contractConfiguration={formData.contractConfiguration}
            onContractConfigurationChange={(config) =>
              setFormData((prev) => ({ ...prev, contractConfiguration: config }))
            }
            isProBono={!!formData.feeConfiguration?.isProBono}
            onProBonoChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                feeConfiguration: { ...(prev.feeConfiguration || {}), isProBono: value },
                contractConfiguration: value ? null : prev.contractConfiguration,
              }))
            }
          />
        </TabsContent>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit">
            {isEditing ? "Atualizar Processo" : "Cadastrar Processo"}
          </Button>
        </div>
      </form>
    </Tabs>
  );
};